import { ActionTree } from 'vuex';
import { SignupService } from '@signing-page/api/signup/services/signup.service';
import { ISignupResponse } from '@signing-page/signing/types/signup-response';
import { EAfterSignStep } from '@signing-page/signing/types/enums/after-sign-step';
import { ESigningStep } from '@signing-page/signing/types/enums/signing-step';
import { ESignErrorCodes } from '@signing-page/signing/types/enums/sign-error-codes';
import { ISigningStep } from '@signing-page/signing/types/signing-step';
import { ISigningState } from '@signing-page/signing/store/signing.types';
import { IRootState } from '@signing-page/store/store';
import { BankIDSEService } from '@signing-page/api/signing/services/bankid-se.service';
import service from '@signing-page/api/documents/services/document.service';

import {
  EBankIdSeStatus,
  EBankIdStep,
  EBankIdSigningType,
  EHintCodeError,
} from '@signing-page/signing/types/enums/bankid-se';
import { SignatureService } from '@signing-page/api/signing/services/signature.service';
import { IDocumentState } from '@signing-page/documents/store/document.store';

import { EDocumentStatus } from '@signing-page/documents/types/enums/document-status';
import { ISignResponse } from '@signing-page/signing/types/sign-response';
import { SigningHelper } from '@signing-page/signing/helpers/signing.helper';
import { ESigningAction, ESigningMutation } from '@signing-page/signing/store/signing.constants';
import { translate } from '@/helpers/translation.helper';
import bugsnagClient from '@lib-shared/bugsnag';
import { userLanguage } from '@signing-page/lang';
import { alertNamespace, EAlertAction } from '@signing-page/alert/store/alert.constants';
import { EFieldAction, fieldNamespace } from '@signing-page/fields/store/field.constants';

const maxAttempts = 180;
const pollTimeOut = 1000;
const completeTimeOut = 5000;

export const actions: ActionTree<ISigningState, IRootState> = {
  [ESigningAction.SetBankIdState](
    { commit },
    { step, signingType }: { step: EBankIdStep; signingType: EBankIdSigningType }
  ) {
    commit(ESigningMutation.SetSigningType, signingType);
    commit('setBankIdSigningStep', step);
  },
  async [ESigningAction.StartBankIdTransaction]({ dispatch, rootState, commit }, ssn = '') {
    if (bugsnagClient) {
      bugsnagClient.leaveBreadcrumb(ESigningAction.StartBankIdTransaction);
    }
    try {
      const { documentId, recipientId } = rootState;
      commit(ESigningMutation.SetBankIdLoading, true);
      const { orderRef, autoStartToken } = await BankIDSEService.start(documentId, ssn);
      await dispatch(ESigningAction.StartBankIdTransactionSuccess, {
        orderRef,
        autoStartToken,
        ssn,
      });
      commit(ESigningMutation.SetBankIdLoading, false);
      dispatch(ESigningAction.PollBankIdTransaction, {
        orderRef,
        documentId,
        recipientId,
      });
    } catch (error) {
      if (bugsnagClient) {
        bugsnagClient.notify(error);
      }
      const errorCode: EHintCodeError = error.response?.status
        ? EHintCodeError.unknownError
        : EHintCodeError.networkError;
      dispatch(
        ESigningAction.StartBankIdTransactionFailed,
        SigningHelper.getBankIdHintCodeTranslation(errorCode)
      );
    }
  },
  [ESigningAction.StartBankIdTransactionSuccess](
    { commit },
    { orderRef, autoStartToken, ssn }: { orderRef: string; autoStartToken: string; ssn: string }
  ) {
    commit(ESigningMutation.SetTransactionData, { autoStartToken, orderRef, ssn });
  },
  [ESigningAction.StartBankIdTransactionFailed]({ dispatch }, error: string) {
    dispatch(ESigningAction.SetError, error);
  },
  async [ESigningAction.CancelBankIdTransaction]({ dispatch, commit, state }, ssn: string) {
    if (ssn) {
      await BankIDSEService.cancel(state.bankId.orderRef);
    }
    dispatch(ESigningAction.SetBankIdState, {
      step: EBankIdStep.Start,
      signingType: EBankIdSigningType.None,
    });

    commit(ESigningMutation.SetTransactionData, { autoStartToken: '', orderRef: '', ssn: '' });
    commit(ESigningMutation.SetAttemptCount, 0);
    commit(ESigningMutation.SetBankIdHintCode, undefined);
  },
  [ESigningAction.RestartPoll](
    { dispatch, state, commit },
    { documentId, recipientId }: { documentId: string; recipientId: string }
  ) {
    commit(ESigningMutation.SetStep, EBankIdStep.Polling);
    dispatch(ESigningAction.PollBankIdTransaction, {
      orderRef: state.bankId.orderRef,
      documentId,
      recipientId,
    });
  },
  async [ESigningAction.PollBankIdTransaction](
    { commit, dispatch, state },
    {
      orderRef,
      documentId,
      recipientId,
    }: {
      orderRef: string;
      documentId: string;
      recipientId: string;
    }
  ) {
    if (bugsnagClient) {
      bugsnagClient.leaveBreadcrumb(ESigningAction.PollBankIdTransaction);
    }
    if (state.bankId.orderRef) {
      try {
        const { status, hintCode } = await BankIDSEService.poll(orderRef, documentId, recipientId);
        // Check if transaction has been cancelled before continuing
        if (!state.bankId.orderRef) {
          return;
        }
        commit(ESigningMutation.SetBankIdHintCode, hintCode);
        switch (status) {
          case EBankIdSeStatus.Pending:
            commit(ESigningMutation.SetAttemptCount, state.bankId.attemptCount + 1);
            if (state.bankId.attemptCount <= maxAttempts) {
              setTimeout(() => {
                dispatch(ESigningAction.PollBankIdTransaction, {
                  orderRef,
                  documentId,
                  recipientId,
                });
              }, pollTimeOut);
            } else {
              dispatch(ESigningAction.PollBankIdTransactionFailed, 'bankid-order-expired');
            }
            break;
          case EBankIdSeStatus.Complete:
            dispatch(ESigningAction.PollBankIdTransactionSuccess);
            break;
          case EBankIdSeStatus.Failed:
            if (
              [EHintCodeError.startFailed, EHintCodeError.noClient].includes(
                hintCode as EHintCodeError
              )
            ) {
              dispatch(ESigningAction.StartBankIdTransaction, state.bankId.ssn);
            } else {
              dispatch(ESigningAction.CheckSignedDocument);
            }
            break;
        }
      } catch (error) {
        if (bugsnagClient) {
          bugsnagClient.notify(error);
        }
        if (state.bankId.orderRef) {
          dispatch(ESigningAction.CheckSignedDocument);
        }
      }
    }
  },
  async [ESigningAction.CheckSignedDocument]({ dispatch, rootState }, error?: any) {
    try {
      const recipients = await service.getRecipients();
      const { recipientId }: IRootState = rootState;
      if (SigningHelper.isSignedByRecipient(recipients, recipientId)) {
        dispatch(ESigningAction.PollBankIdTransactionSuccess);
      } else {
        const errorCode: EHintCodeError = error.response?.status
          ? EHintCodeError.unknownError
          : EHintCodeError.networkError;
        dispatch(
          ESigningAction.PollBankIdTransactionFailed,
          SigningHelper.getBankIdHintCodeTranslation(errorCode)
        );
      }
    } catch (error) {
      if (bugsnagClient) {
        bugsnagClient.notify(error);
      }
      const errorCode: EHintCodeError = error.response?.status
        ? EHintCodeError.unknownError
        : EHintCodeError.networkError;
      dispatch(
        ESigningAction.PollBankIdTransactionFailed,
        SigningHelper.getBankIdHintCodeTranslation(errorCode)
      );
    }
  },
  [ESigningAction.PollBankIdTransactionFailed]({ dispatch }, error: string) {
    dispatch(ESigningAction.SetError, error);
  },
  [ESigningAction.SetError]({ commit }, error: string) {
    commit(ESigningMutation.SetBankIdHintCode, undefined);
    commit(ESigningMutation.SetError, error);
    commit(ESigningMutation.SetStep, EBankIdStep.Failed);
    commit(ESigningMutation.SetSigningType, EBankIdSigningType.None);
    commit(ESigningMutation.SetBankIdLoading, false);
  },
  [ESigningAction.PollBankIdTransactionSuccess]({ commit }) {
    commit(ESigningMutation.SetStep, EBankIdStep.Signed);
    commit(ESigningMutation.SetBankIdHintCode, undefined);
    setTimeout(() => {
      commit(ESigningMutation.SetStep, EBankIdStep.Completed);
    }, completeTimeOut);
  },
  [ESigningAction.ResetBankId]({ commit }) {
    commit(ESigningMutation.ResetBankId);
  },
  async [ESigningAction.CheckVerificationStep]({ commit, dispatch, rootState }) {
    try {
      commit(ESigningMutation.SetIsLoading, true);
      const { recipientId, authId }: IRootState = rootState;
      const {
        document: { id: documentId },
      }: IDocumentState = rootState['document'];
      const step: ISigningStep = await SignatureService.checkVerificationStep(
        recipientId,
        documentId,
        authId
      );
      dispatch(ESigningAction.CheckVerificationStepSuccess, step);
    } catch (e) {
      commit(ESigningMutation.SetIsLoading, false);
    }
  },
  [ESigningAction.CheckVerificationStepSuccess]({ commit }, step: ISigningStep) {
    commit(ESigningMutation.SetIsLoading, false);
    commit(ESigningMutation.SetSigningStep, step);
  },
  [ESigningAction.CheckVerificationStepFailed]({ dispatch, commit }) {
    commit(ESigningMutation.SetIsLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-verify-signature'),
      { root: true }
    );
  },
  [ESigningAction.SetBankIdQueryParams](
    { commit },
    {
      orderRef,
      step,
      signingType,
      autoStartToken,
    }: {
      orderRef: string;
      step: EBankIdStep;
      signingType: EBankIdSigningType;
      autoStartToken: string;
    }
  ) {
    commit(ESigningMutation.SetSigningStep, ESigningStep.Eid);
    commit(ESigningMutation.SetStep, step);
    commit(ESigningMutation.SetTransactionData, { orderRef, ssn: '', autoStartToken });
    commit(ESigningMutation.SetSigningType, signingType);
  },
  async [ESigningAction.VerifySignature](
    { dispatch, rootState, commit },
    { signatureBase30, signatureBase64 }: { signatureBase30: string; signatureBase64: string }
  ) {
    if (!signatureBase30 || !signatureBase64) {
      dispatch(ESigningAction.VerifySignatureFailed);
      return;
    }
    commit(ESigningMutation.SetVerifyingSignature, true);
    const { recipientId, authId }: IRootState = rootState;
    const {
      document: { id: documentId },
    }: IDocumentState = rootState['document'];
    try {
      const base64Verified: boolean = await SignatureService.verifySignatureBase64(signatureBase64);
      if (!base64Verified) {
        dispatch(ESigningAction.VerifySignatureFailed);
        return;
      }
      const base30Verified: boolean = await SignatureService.verifySignatureBase30(
        recipientId,
        documentId,
        authId,
        signatureBase30
      );
      if (!base30Verified) {
        dispatch(ESigningAction.VerifySignatureFailed);
        return;
      }
      dispatch(ESigningAction.VerifySignatureSuccess);
    } catch (error) {
      dispatch(ESigningAction.VerifySignatureFailed);
    }
  },
  [ESigningAction.VerifySignatureSuccess]({ dispatch, commit }) {
    const timeout = 2000;
    setTimeout(() => {
      dispatch(ESigningAction.CheckVerificationStep);
      commit(ESigningMutation.SetVerifyingSignature, false);
    }, timeout);
  },
  [ESigningAction.VerifySignatureFailed]({ dispatch, commit }) {
    commit(ESigningMutation.SetVerifyingSignature, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-verify-signature'),
      { root: true }
    );
  },
  async [ESigningAction.VerifyQna]({ dispatch, commit, rootState }, answer: string) {
    try {
      commit(ESigningMutation.SetVerifyingQna, true);
      const { recipientId, authId }: IRootState = rootState;
      const {
        document: { id: documentId },
      }: IDocumentState = rootState['document'];
      const success: boolean = await SignatureService.verifyQna(
        answer,
        documentId,
        recipientId,
        authId
      );
      if (success) {
        dispatch(ESigningAction.VerifyQnaSuccess);
      } else {
        dispatch(ESigningAction.VerifyQnaFailed, '');
      }
    } catch (e) {
      dispatch(ESigningAction.VerifyQnaFailed, e);
    }
  },
  [ESigningAction.VerifyQnaFailed]({ commit }) {
    commit(ESigningMutation.SetVerifyingQna, false);
    commit(ESigningMutation.SetQnaError, true);
  },
  [ESigningAction.VerifyQnaSuccess]({ commit, dispatch }) {
    commit(ESigningMutation.SetVerifyingQna, false);
    commit(ESigningMutation.SetQnaError, false);

    dispatch(ESigningAction.CheckVerificationStep);
  },
  async [ESigningAction.SendSmsCode]({ dispatch, commit, rootState, state }) {
    try {
      commit(ESigningMutation.SetSmsError, false);
      commit(ESigningMutation.SetVerifyingSms, false);
      const { recipientId, authId }: IRootState = rootState;
      const {
        document: { id: documentId },
      }: IDocumentState = rootState['document'];
      await SignatureService.sendSmsCode(
        documentId,
        recipientId,
        authId,
        state.sms.sendPinRetryCount
      );
      commit(ESigningMutation.SetSendPinRetryCount, state.sms.sendPinRetryCount + 1);
    } catch (e) {
      dispatch(ESigningAction.SendSmsCodeFailed);
    }
  },
  [ESigningAction.SendSmsCodeFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-send-sms-code'), {
      root: true,
    });
  },
  async [ESigningAction.VerifySms]({ dispatch, commit, rootState }, pin: string) {
    try {
      commit(ESigningMutation.SetVerifyingSms, true);
      const { recipientId, authId }: IRootState = rootState;
      const {
        document: { id: documentId },
      }: IDocumentState = rootState['document'];
      const success: boolean = await SignatureService.verifySmsCode(
        pin,
        documentId,
        recipientId,
        authId
      );
      if (success) {
        dispatch(ESigningAction.VerifySmsSuccess);
      } else {
        dispatch(ESigningAction.VerifySmsFailed, '');
      }
    } catch (e) {
      dispatch(ESigningAction.VerifySmsFailed, e);
    }
  },
  [ESigningAction.VerifySmsSuccess]({ commit, dispatch }) {
    commit(ESigningMutation.SetVerifyingSms, false);
    commit(ESigningMutation.SetSmsError, false);

    dispatch(ESigningAction.CheckVerificationStep);
    commit(ESigningMutation.SetIsLoading, false);
  },
  [ESigningAction.VerifySmsFailed]({ commit }) {
    commit(ESigningMutation.SetVerifyingSms, false);
    commit(ESigningMutation.SetSmsError, true);
  },
  async [ESigningAction.SignDocument]({ commit, dispatch, rootState, state }, signInitials = '') {
    try {
      commit(ESigningMutation.SetVerifyingSign, true);
      const { initials }: ISigningState = state;
      const { recipientId, authId }: IRootState = rootState;
      const {
        document: { id: documentId },
      }: IDocumentState = rootState['document'];
      const response: ISignResponse = await SignatureService.sign(
        initials || signInitials,
        documentId,
        recipientId,
        authId
      );
      switch (response.status) {
        case 1:
          dispatch(ESigningAction.SignDocumentSuccess, response.signeesWaiting);
          break;
        case -1:
          dispatch(ESigningAction.SignDocumentFailed, ESignErrorCodes.Initials);
          break;
        default:
          dispatch(ESigningAction.SignDocumentFailed, ESignErrorCodes.Other);
      }
    } catch (e) {
      dispatch(ESigningAction.SignDocumentFailed, ESignErrorCodes.Other);
    }
  },
  async [ESigningAction.SignDocumentSuccess](
    { commit, dispatch, rootState },
    signeesWaiting: number
  ) {
    const { recipientId }: IRootState = rootState;
    dispatch('document/completeSigning', { signeesWaiting, recipientId }, { root: true });
    commit(ESigningMutation.SetVerifyingSign, false);
  },
  [ESigningAction.SignDocumentFailed]({ commit }, code: ESignErrorCodes) {
    commit(ESigningMutation.SetVerifyingSign, false);
    console.error('COULD NOT SIGN DOCUMENT: ', code);
  },
  async [ESigningAction.ApproveDocument]({ rootState, dispatch }) {
    try {
      const { recipientId, authId }: IRootState = rootState;
      const {
        document: { id: documentId },
      }: IDocumentState = rootState['document'];
      const success: boolean = await SignatureService.approve(documentId, recipientId, authId);
      if (success) {
        dispatch(ESigningAction.ApproveDocumentSuccess, recipientId);
      } else {
        dispatch(ESigningAction.ApproveDocumentFailed);
      }
    } catch (e) {
      dispatch(ESigningAction.ApproveDocumentFailed);
    }
  },
  [ESigningAction.ApproveDocumentSuccess]({ dispatch }, recipientId) {
    dispatch('document/approveDocument', { recipientId }, { root: true });
    dispatch('document/sendPusherStatus', { status: EDocumentStatus.Approved }, { root: true });
  },
  [ESigningAction.ApproveDocumentFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-approve-document'),
      { root: true }
    );
  },
  [ESigningAction.LogAfterSignStep](step: any) {
    //TODO: In future. Add log where the user drops off.
    console.log(step);
  },
  async [ESigningAction.CreateSignup](
    { dispatch, commit },
    { email, campaign }: { email: string; campaign: string }
  ) {
    try {
      commit(ESigningMutation.SetAfterSign, { loading: true });
      const response: ISignupResponse = await SignupService.signup(email, campaign, userLanguage());
      if (response && response.redirectUrl) {
        dispatch(ESigningAction.CreateSignupSuccess, response.redirectUrl);
        dispatch(ESigningAction.SetAfterSignStep, EAfterSignStep.SignupComplete);
      } else if (response && response.error) {
        commit(ESigningMutation.SetAfterSign, {
          error: response.error,
          didYouMean: response.didYouMean,
          loading: false,
        });
        dispatch(ESigningAction.CreateSignupFailed);
      }
    } catch {
      dispatch(ESigningAction.CreateSignupFailed);
    }
  },
  [ESigningAction.SetAfterSignStep]({ commit }, step: EAfterSignStep) {
    commit(ESigningMutation.SetAfterSign, { step });
  },
  [ESigningAction.CreateSignupSuccess]({ commit }, redirectUrl: string) {
    commit(ESigningMutation.SetAfterSign, {
      redirectUrl,
      error: '',
      didYouMean: '',
      loading: false,
    });
  },
  [ESigningAction.CreateSignupFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-create-account'),
      { root: true }
    );
  },
  async [ESigningAction.SendFeedback](
    { dispatch },
    { feedback, email }: { feedback: string; email: string }
  ) {
    try {
      await SignupService.feedback(feedback, email);
    } catch {
      dispatch(ESigningAction.SendFeedbackFailed);
    }
  },
  [ESigningAction.SendFeedbackFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-send-feedback'), {
      root: true,
    });
  },
  [ESigningAction.AddInitials](
    { commit, dispatch },
    { initials, fieldId }: { initials: string; fieldId: string }
  ) {
    commit(ESigningMutation.SetInitials, initials);
    dispatch(ESigningAction.SetSelectedInitialFieldId, '');
    dispatch(`${fieldNamespace}/${EFieldAction.AddVerifiedInitialId}`, fieldId, { root: true });
  },
  [ESigningAction.SetSelectedInitialFieldId]({ commit }, selectedInitialFieldId: string) {
    commit(ESigningMutation.SetSelectedInitialFieldId, selectedInitialFieldId);
  },
};
